import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router';
import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useQuery } from '@tanstack/react-query';
import TwoLevelTabNavigation from 'components/Navigation/TwoLevelTabNavigation';
import { GlobalState } from 'reducers/stateTypes';
import { selectShowSecondLevelTabs } from 'selectors/partnerFormSelectors';
import { User } from 'types/User';
import { TabContext } from '@mui/lab';
import { AppBar, Box, Button, Grid, Skeleton, SvgIcon, Tooltip, Typography } from '@mui/material';
import palette from 'theme/palette';
import EmbeddedIFrame from 'views/Frames/EmbeddedIFrame';
import { ONE_LEVEL_NAV_FRAME_HEIGHT, TWO_LEVEL_NAV_FRAME_HEIGHT } from 'services/constants';
import PartnerFrontendIFrame from 'views/Frames/PartnerFrontendIFrame';
import { setTabId } from 'reducers/partnerFormReducer';
import { ReactComponent as StructureIcon } from 'assets/icons/Structure.svg';
import { useFetchLegalPartner } from 'hooks/queries/useFetchPartner';
import { usePartnerDlmV2Enabled } from 'hooks/queries/usePartnerDlmV2Enabled';
import { ReactComponent as GlobalST } from 'assets/icons/ST_Global.svg';
import RejectWithdrawButton from 'views/PartnerTabs/RejectWithdrawButton';
import { PartnerType } from 'types/Partner';
import { openModal } from 'reducers/modalReducer';
import { getPartnerProfile } from 'services/partnerOrchestratorService';
import { BrContext, BrProfileType, BrType } from 'types/BrTypes';
import { PartnerFormCategories, PartnerFormTabItems } from 'views/PartnerTabs/PrivateIndividualFormTabs';
import { useFetchBusinessRelations } from 'hooks/queries/useFetchClmBrs';
import PartnerTabsMessageHandler from 'views/PartnerTabs/PartnerTabsMessageHandler';
import { partnerTabStyles } from 'views/PartnerTabs/LegalPartnerFormTabs';
import { useRuntimeConfig } from 'config/useRuntimeConfig';
import { assertNotNullOrUndefined, isNotNullOrUndefined, isNullOrUndefined, isStringNotBlank } from 'utils/utils';
import { selectDlmStatus, selectDlmVersion } from 'selectors/dlmSelector';
import { JBColors, JBIcon } from '@bjb-gcrm/gcrm-frontend-components';
import { useFetchPartnerLockStatus } from 'hooks/queries/useFetchPartnerLockStatus';
import { Lock } from 'views/Lock/Lock';
import { DlmStatus } from 'views/DraftMode/DraftSwitch';
import { usePostMessageToIFrame } from 'hooks/usePostMessageToIFrame';
import { ClmPortalIncomingPostMessageTypes, ClmPortalOutgoingPostMessageTypes } from 'utils/postMessageUtil';
import { useDetectMultipleTabs } from 'hooks/useDetectMultipleTabs';
import { formatEnum, getPartnerPageTitle } from 'utils/stringUtil';
import theme from 'theme';
import { MultiTabWarning } from 'components/Common/MultiTabWarning';
import { useFetchPartnerVersions } from 'hooks/queries/useFetchPartnerVersions';
import { resetDlmState } from 'reducers/dlmReducer';
import PageTitleToken from 'components/Common/PageTitleToken';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import { SanctionsPartnerType } from 'views/PartnerTabs/SanctionsPartnerType';

/**
 * The switchable tabs on top of the Legal Partner form.
 */
const StructureFormTabs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tabId = useSelector((state: GlobalState) => state.partnerForm.tabIndex);
  const dispatch = useDispatch();
  const { structureType, id } = useParams<{
    structureType: string;
    id: string;
  }>();
  const hasMultipleTabs = useDetectMultipleTabs(id);

  const user: User = useSelector((state: GlobalState) => state.user.userDetails);
  const userId: string = user?.username;
  const isUserApac: boolean = user?.isApac;
  assertNotNullOrUndefined(id);
  const techId: string | undefined = id;
  const location = useLocation();
  const queryParams = new URLSearchParams(location?.search);
  const urlHash = location.hash;
  const distributeDocument = queryParams.get('distributeDocument');
  const clientClassificationId = queryParams.get('clientClassificationId');
  const urlSearchDocumentId = queryParams.get('documentId');
  const wizardBrUid = queryParams.get('wizardBrUid');
  const wizardRole = queryParams.get('wizardRole');
  const { data: partnerVersions = [], isFetching: isVersionsFetching } = useFetchPartnerVersions(techId);

  const runtimeConfig = useRuntimeConfig();
  const baseDocUrlSearchParam = isStringNotBlank(urlSearchDocumentId) ? `?documentId=${urlSearchDocumentId}` : '';
  const basePcdV2Url = `/partner/${techId}`;
  const [pcdV2DistributionUrl, setPcdV2DistributionUrl] = useState(`${basePcdV2Url}/distributions-overview`);
  const [pcdV2DocOverviewUrl] = useState(`${basePcdV2Url}/documents-overview${baseDocUrlSearchParam}`);
  const newNote = queryParams?.get('newNote');
  const caseId = useSelector((state: GlobalState) => state.caseInformation.caseId);
  const { data: partner, isFetching, refetch } = useFetchLegalPartner(techId);
  const { data: brItems } = useFetchBusinessRelations(techId, true, partner && partner.imported);
  const partnerName = partner?.name ?? 'New structure';
  const partnerHasIntermediaryBrs = brItems && brItems.length > 0;
  const isContactNotesTabEnabled =
    runtimeConfig.configData.featureFlags?.contactNote?.enabled || !partner?.imported || partnerHasIntermediaryBrs;
  const pcoUrl: string = caseId ? `/partner/${techId}/case/${caseId}/update` : `/partner?partnerNumber=${techId}`;
  const { tab = PartnerFormTabItems.CORE_DATA } = useParams<{ tab: PartnerFormTabItems }>();
  const tabRef = useRef(tab);
  const tabsNeedingSaveValidation = [
    PartnerFormTabItems.CORE_DATA,
    PartnerFormTabItems.RELATIONS,
    PartnerFormTabItems.CONTACT_NOTES,
    PartnerFormTabItems.RISK_CONSIDERATIONS,
    PartnerFormTabItems.INVESTMENT_PROFILE,
    PartnerFormTabItems.PARTNER_PROFILE,
    PartnerFormTabItems.WEALTH_OVERVIEW,
  ];
  const [intendedRole, setIntendedRole] = useState('');
  const [brProfile, setBrProfile] = useState<BrProfileType | undefined>(undefined);
  const [brContext, setBRContext] = useState<BrContext | null>(null);
  const brType = brContext?.brType ?? '';
  const [selectedBrContext, setSelectedBrContext] = useState(brContext);
  const [selectedIntendedRole, setSelectedIntendedRole] = useState<string>('');
  const [selectedProfileCode, setSelectedProfileCode] = useState<string>('');
  const { postMessageToIFrameByType, postSetPartnerProfileMessage } = usePostMessageToIFrame();

  const { data: partnerLocks = [] } = useFetchPartnerLockStatus(techId);
  const isPartnerLockEnabled = runtimeConfig.configData.featureFlags?.partnerLock?.enabled;
  const isPartnerDlmV2 = usePartnerDlmV2Enabled();
  const partnerStructureSubType = isUserApac ? undefined : partner?.partnerStructureSubType;
  const { data: partnerProfile } = useQuery({
    queryKey: [
      'getPartnerProfile',
      intendedRole,
      brType,
      brProfile,
      partnerStructureSubType,
      brContext?.brSubType,
      partner?.operationalType,
      partner?.partnerType,
    ],
    queryFn: () =>
      getPartnerProfile(
        brType as BrType,
        intendedRole,
        partner?.partnerType,
        partnerStructureSubType,
        brContext?.brSubType,
        partner?.operationalType,
        brProfile,
      ),
    enabled: isNotNullOrUndefined(intendedRole) && isNotNullOrUndefined(brType),
  });

  const isPartnerProfileLoading = !!intendedRole && !partnerProfile;
  const profilingWizardParams =
    !!partner && !intendedRole && brType
      ? ''
      : `?intendedRole=${intendedRole ?? ''}&profileCode=${partnerProfile ?? ''}&brType=${brType ?? ''}&brMasterNumber=${brContext?.busRelMasterNumber ?? ''}&busRelUid=${brContext?.busRelUid ?? ''}&brSubType=${brContext?.brSubType ?? ''}&partnerStructureSubType=${partner?.partnerStructureSubType ?? ''}&partnerType=${partner?.partnerType ?? ''}&brProfile=${brProfile ?? ''}&operationalType=${partner?.operationalType ?? ''}`;
  const partnerProfileQueryParams = new URLSearchParams(
    profilingWizardParams.startsWith('?') ? profilingWizardParams.slice(1) : profilingWizardParams,
  );
  if (isStringNotBlank(distributeDocument)) {
    partnerProfileQueryParams.set('distributeDocument', distributeDocument);
  }
  if (isStringNotBlank(clientClassificationId)) {
    partnerProfileQueryParams.set('clientClassificationId', clientClassificationId);
  }
  const partnerProfileQueryString = partnerProfileQueryParams.toString();
  const partnerProfileQuerySuffix = isStringNotBlank(partnerProfileQueryString) ? `?${partnerProfileQueryString}` : '';
  const dlmStatus = useSelector(selectDlmStatus);
  const dlmVersion = useSelector(selectDlmVersion);
  const [initialDlmStatusAndVersion, setInitialDlmStatusAndVersion] = useState<{
    status: DlmStatus;
    version: number;
  } | null>(null);
  // DLM v2: the iframe resolves its own status/version, the portal passes none.
  const dlmUrlParams = isPartnerDlmV2
    ? ''
    : `&partnerStatus=${initialDlmStatusAndVersion?.status}&version=${initialDlmStatusAndVersion?.version}`;
  const showSecondLevelTabs = useSelector(selectShowSecondLevelTabs);
  const frameHeight = showSecondLevelTabs ? TWO_LEVEL_NAV_FRAME_HEIGHT : ONE_LEVEL_NAV_FRAME_HEIGHT;
  const isProfilingWizardBrChangesEnabled = runtimeConfig.configData.featureFlags?.profilingWizardBrChanges?.enabled;
  const isUatEnvironment = runtimeConfig.configData.env === 'pcm-uat1' || runtimeConfig.configData.env === 'pcm-uat2';

  useEffect(() => {
    if (
      isNullOrUndefined(initialDlmStatusAndVersion) &&
      isNotNullOrUndefined(dlmStatus) &&
      isNotNullOrUndefined(dlmVersion)
    ) {
      setInitialDlmStatusAndVersion({ status: dlmStatus, version: dlmVersion });
    }
  }, [dlmStatus, dlmVersion]);

  useEffect(() => {
    if (
      isPartnerDlmV2 !== false ||
      isNullOrUndefined(initialDlmStatusAndVersion) ||
      isNullOrUndefined(dlmStatus) ||
      isNullOrUndefined(dlmVersion) ||
      isNullOrUndefined(partnerVersions) ||
      isVersionsFetching
    ) {
      return;
    }

    // Validate that the exact status/version pair still exists
    const matchingVersion = partnerVersions.find((x) => x.partnerStatus === dlmStatus && x.version === dlmVersion);

    // During discard/recreate flow the old draft may already be discarded.
    // In that case do NOT trigger a stale RE_FETCH_PARTNER call.
    if (isNullOrUndefined(matchingVersion)) {
      return;
    }

    postMessageToIFrameByType(ClmPortalOutgoingPostMessageTypes.RE_FETCH_PARTNER, {
      partnerStatus: dlmStatus,
      version: dlmVersion,
    });
  }, [dlmStatus, dlmVersion, partnerVersions, isVersionsFetching, initialDlmStatusAndVersion]);

  useEffect(() => {
    if (isNotNullOrUndefined(dlmStatus) && isNotNullOrUndefined(dlmVersion)) {
      setInitialDlmStatusAndVersion({ status: dlmStatus, version: dlmVersion });
    }
  }, [tab]);

  useEffect(() => {
    if (isNotNullOrUndefined(partnerProfile)) {
      postSetPartnerProfileMessage(partnerProfile);
    }
  }, [partnerProfile, intendedRole, brType, selectedIntendedRole, selectedBrContext]);

  useEffect(() => {
    if (Object.values(PartnerFormTabItems).includes(tab)) {
      dispatch(setTabId(tab));
    }
  }, []);

  useEffect(() => {
    return () => {
      dispatch(resetDlmState());
      //Reset tab on unmount
      dispatch(setTabId(PartnerFormTabItems.CORE_DATA));
    };
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleMessage, false);

    function handleMessage(event: any) {
      const { message, clearPcdUrl } = event.data;
      if (clearPcdUrl) {
        setPcdV2DistributionUrl(`${basePcdV2Url}/distributions-overview`);
      }
      switch (message) {
        case ClmPortalIncomingPostMessageTypes.REDIRECT_DOC_TAB_V2: {
          const { distributionRequestId, initiator } = event.data.value;
          setPcdV2DistributionUrl(`/distribution/${distributionRequestId}?initiator=${initiator}`);
          dispatch(setTabId(PartnerFormTabItems.DISTRIBUTION));
          break;
        }
        case ClmPortalIncomingPostMessageTypes.UPDATE_INTENDED_ROLE: {
          setIntendedRole(event.data.value.roleType);

          const receivedBrContext = isProfilingWizardBrChangesEnabled
            ? event.data.value.brType
            : {
                brType: event.data.value.brType,
                brSubType: '',
                busRelMasterNumber: '',
                busRelUid: '',
              };
          setBRContext(receivedBrContext);
          setBrProfile(event.data.value.brProfileType);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.CHANGE_TAB: {
          changeTab();

          break;
        }
        case ClmPortalIncomingPostMessageTypes.SAVED_PARTNER: {
          refetch();

          break;
        }
        case ClmPortalIncomingPostMessageTypes.CHANGE_TAB_PARTNER_PROFILE: {
          dispatch(setTabId(PartnerFormTabItems.PARTNER_PROFILE));
          // When returning from PCD (clearPcdUrl === true), strip the distribution modal params
          // from the browser URL so the partner-profile iframe does not reopen the modal on reload.
          if (clearPcdUrl) {
            const currentParams = new URLSearchParams(location.search);
            currentParams.delete('distributeDocument');
            currentParams.delete('clientClassificationId');
            const newSearch = currentParams.toString();
            navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}${location.hash ?? ''}`, {
              replace: true,
            });
          }
          break;
        }
        case ClmPortalIncomingPostMessageTypes.CHANGE_TAB_INVESTMENT_PROFILE: {
          dispatch(setTabId(PartnerFormTabItems.INVESTMENT_PROFILE));

          break;
        }
        case ClmPortalIncomingPostMessageTypes.OPEN_NEW_BR_TAB: {
          const { brId, brTab, hash } = event.data.value;
          window.open(`/business-relation/edit/${brId}/${brTab}${isNotNullOrUndefined(hash) ? '#' + hash : ''}`);
          break;
        }
        // No default
      }
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [brType, intendedRole, selectedIntendedRole, selectedBrContext, isProfilingWizardBrChangesEnabled]);

  const handleTabChange = async (_event: SyntheticEvent, newValue: React.SetStateAction<any>) => {
    if (tabsNeedingSaveValidation.includes(tabId)) {
      postMessageToIFrameByType(ClmPortalOutgoingPostMessageTypes.CHANGE_TAB_SAVE_CHECK);
      tabRef.current = newValue;
    } else {
      tabRef.current = newValue;
      changeTab();
    }
  };

  const persistReceivedValuesFromIFrame = () => {
    setSelectedBrContext(brContext);
    setSelectedIntendedRole(intendedRole);
    setSelectedProfileCode(partnerProfile);
  };

  const changeTab = (hash?: string) => {
    if (tabRef.current !== tab) {
      persistReceivedValuesFromIFrame();
    }
    dispatch(setTabId(tabRef.current));
    navigate(`/partner/structure/edit/${techId}/${tabRef.current}${hash ?? ''}`, { replace: true });
  };

  const { onClick: copyOnClick, feedbackMessage: copyToClipboardFeedbackMessage } = useCopyToClipboard();

  return (
    <>
      <PageTitleToken alternateTitle={getPartnerPageTitle(isUatEnvironment, partner?.globalPartnerId)} />
      <TabContext value={tabId}>
        <PartnerTabsMessageHandler />
        <AppBar position="static" sx={partnerTabStyles.topBar} elevation={0}>
          <Grid container>
            <Grid item xs={7}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  minHeight: '68px',
                }}
              >
                {!isFetching && hasMultipleTabs && (
                  <MultiTabWarning
                    tooltipText={t('multipleTabs.warning', { type: t('multipleTabs.warning.partner') })}
                  />
                )}
                <Typography
                  variant="h1"
                  color={JBColors.white}
                  sx={{
                    backgroundColor: JBColors.core.reflexBlue['50'],
                    paddingX: '8px',
                    borderRadius: '2px',
                  }}
                  data-testid="partner-label-icon"
                >
                  {t('partner-label-icon')}
                </Typography>
                <Tooltip
                  arrow
                  title={
                    isNotNullOrUndefined(copyToClipboardFeedbackMessage) ? copyToClipboardFeedbackMessage : partnerName
                  }
                  componentsProps={{
                    tooltip: {
                      'data-testid': 'partner-title-tooltip',
                    } as any,
                  }}
                >
                  <Typography
                    variant="h1"
                    color="primary"
                    sx={{ ...partnerTabStyles.title, ...partnerTabStyles.cursorPointer }}
                    title={partnerName}
                    onClick={() => copyOnClick(partnerName)}
                    data-testid="partner-name-header"
                  >
                    {isFetching ? <Skeleton variant="text" width="250px" /> : partnerName}
                  </Typography>
                </Tooltip>
                {partner?.globalPartner ? (
                  <SvgIcon sx={partnerTabStyles.titleIcon}>
                    <GlobalST fill={palette.primary.main} id="global-partner-icon" />
                  </SvgIcon>
                ) : (
                  <SvgIcon sx={{ ...partnerTabStyles.titleIcon, marginTop: theme.spacing(1) }}>
                    <StructureIcon fill={palette.primary.main} />
                  </SvgIcon>
                )}
                <Typography variant="body1" sx={partnerTabStyles.superscript}>
                  {isNotNullOrUndefined(partner?.legalPartnerSubType)
                    ? formatEnum(partner?.legalPartnerSubType)
                    : structureType?.toUpperCase()}
                </Typography>
                <Typography variant="h5" sx={partnerTabStyles.partnerId}>
                  {partner ? `(${partner?.globalPartnerId})` : ''}
                </Typography>
                <SanctionsPartnerType />
              </Box>
            </Grid>
            <Grid item xs={5}>
              <Grid
                container
                direction="row"
                justifyContent="flex-end"
                alignContent="center"
                alignItems="center"
                sx={{ height: '100%' }}
                gap={2}
              >
                <Grid item xs={9} md={7} lg={9}>
                  <Lock
                    techId={techId}
                    partnerLocks={partnerLocks}
                    shouldShowDraftSwitch={tabId === PartnerFormTabItems.CORE_DATA && isNotNullOrUndefined(userId)}
                  />
                </Grid>
                <Grid item>
                  <Button
                    sx={{ maxHeight: '32px' }}
                    id="revision-history-button"
                    variant="outlined"
                    data-testid="revision-history-button"
                    onClick={() =>
                      dispatch(
                        openModal({
                          modalType: isPartnerLockEnabled ? 'RevisionHistoryModal' : 'EditPartnerHistoryModal',
                          modalProps: {
                            partnerType: PartnerType.LEGAL_PARTNER,
                            techId,
                          },
                        }),
                      )
                    }
                  >
                    <JBIcon iconName="changeHistory" size="md" fill={JBColors.core.reflexBlue[100]} />
                  </Button>
                </Grid>
                <Grid item>
                  <RejectWithdrawButton
                    partnerType={PartnerType.LEGAL_PARTNER}
                    isPartnerLockEnabled={!!isPartnerLockEnabled}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </AppBar>

        <TwoLevelTabNavigation
          onTabChange={handleTabChange}
          selectedTab={tabId}
          secondLevelBackgroundColor="#E3E4F2"
          tabItems={[
            {
              value: PartnerFormTabItems.CORE_DATA,
              label: t('partnerTabs.menu.coreData', 'Core data'),
              category: PartnerFormCategories.PROFILE,
            },
            {
              value: PartnerFormTabItems.PARTNER_PROFILE,
              label: t('partnerTabs.menu.partnerProfile', 'Partner profile'),
              category: PartnerFormCategories.PROFILE,
            },
            {
              value: PartnerFormTabItems.WEALTH_OVERVIEW,
              label: t('partnerTabs.menu.wealthProfile', 'Wealth profile'),
              category: PartnerFormCategories.PROFILE,
            },
            {
              value: PartnerFormTabItems.RISK_CONSIDERATIONS,
              label: t('partnerTabs.menu.riskConsiderations', 'Risk considerations'),
              category: PartnerFormCategories.PROFILE,
            },
            {
              value: PartnerFormTabItems.INVESTMENT_PROFILE,
              label: t('partnerTabs.menu.investmentProfile', 'Investment profile'),
              hidden: isUserApac,
              category: PartnerFormCategories.PROFILE,
            },
            {
              value: PartnerFormTabItems.ACCOUNT_RELATIONS,
              label: t('partnerTabs.menu.p2br', 'Business relations'),
              hidden: isUserApac,
              category: PartnerFormCategories.RELATIONSHIPS,
            },
            {
              value: PartnerFormTabItems.RELATIONS,
              label: t('partnerTabs.menu.p2p', 'Partner relations'),
              hidden: isUserApac,
              category: PartnerFormCategories.RELATIONSHIPS,
            },
            {
              value: PartnerFormTabItems.CONTACT_NOTES,
              label: t('partnerTabs.menu.contactNotes', 'Contact notes'),
              hidden: !isContactNotesTabEnabled,
              category: PartnerFormCategories.INTERACTIONS,
              iconPosition: 'end',
              sx: { marginRight: '30px' },
            },
            {
              value: PartnerFormTabItems.ACTIVITY_TIMELINE,
              label: t('partnerTabs.menu.activityTimeline', 'Activity timeline'),
              hidden: isUserApac,
              category: PartnerFormCategories.INTERACTIONS,
            },
            {
              value: PartnerFormTabItems.DOCUMENTS,
              label: t('partnerTabs.menu.distributionCenter', 'Distribution center'),
              hidden: isUserApac,
              category: PartnerFormCategories.DOCUMENTS,
            },
            {
              value: PartnerFormTabItems.DISTRIBUTION,
              label: t('partnerTabs.menu.distribution', 'Distribution'),
              hidden: isUserApac,
              category: PartnerFormCategories.DOCUMENTS,
            },
            {
              value: PartnerFormTabItems.DOCUMENT_OVERVIEW,
              label: t('partnerTabs.menu.documentOverview', 'Document overview'),
              hidden: isUserApac,
              category: PartnerFormCategories.DOCUMENTS,
            },
            {
              value: PartnerFormTabItems.DOC_COLLECTION,
              label: t('partnerTabs.menu.docCollection', 'Doc collection'),
              hidden: isUserApac,
              category: PartnerFormCategories.DOCUMENTS,
            },
            {
              value: PartnerFormTabItems.PRODUCTS_AND_SERVICES,
              label: t('tabs.menu.categories.products-and-services', 'Products & services'),
              hidden: isUserApac,
              category: PartnerFormCategories.PRODUCTS_AND_SERVICES,
            },
          ]}
        />

        {techId && tabId === PartnerFormTabItems.CORE_DATA && (
          <PartnerFrontendIFrame
            path={`/partner/structure/edit/${techId}/core-data?brType=${selectedBrContext?.brType ?? ''}&brMasterNumber=${selectedBrContext?.busRelMasterNumber ?? ''}&busRelUid=${selectedBrContext?.busRelUid ?? ''}&intendedRole=${selectedIntendedRole}&profileCode=${
              selectedProfileCode ?? ''
            }${dlmUrlParams}&wizardBrUid=${wizardBrUid ?? ''}&wizardRole=${wizardRole ?? ''}`}
            loading={
              isPartnerDlmV2 === undefined ||
              (isPartnerLockEnabled &&
                !isPartnerDlmV2 &&
                isNullOrUndefined(initialDlmStatusAndVersion) &&
                isNotNullOrUndefined(user))
            }
            heightOffset={showSecondLevelTabs ? '218px' : '174px'}
          />
        )}

        {techId && tabId === PartnerFormTabItems.PARTNER_PROFILE && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.partnerProfileIframe}/partner-profile/partner/${techId}${partnerProfileQuerySuffix}${isNotNullOrUndefined(urlHash) ? urlHash : ''}`}
            title={'Partner profile'.toUpperCase()}
            id="pp-iframe"
            height={frameHeight}
            loading={isPartnerProfileLoading}
          />
        )}

        {techId && tabId === PartnerFormTabItems.WEALTH_OVERVIEW && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.wealthOverviewIframe}/wealth-overview/partner/${techId}${profilingWizardParams}`}
            title={'Wealth Overview'.toUpperCase()}
            id="wo-iframe"
            height={frameHeight}
          />
        )}

        {techId && tabId === PartnerFormTabItems.INVESTMENT_PROFILE && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.investmentProfileIframe}/investment-profile/partner?partnerId=${techId}`}
            title={'Investment profile'.toUpperCase()}
            id="structure-investment-profile-iframe"
            height={frameHeight}
          />
        )}

        {tabId === PartnerFormTabItems.CONTACT_NOTES && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.contactNoteIframe}/contact-notes/${techId}${isStringNotBlank(newNote) ? '/create' : ''}`}
            title="Contact notes"
            id="contact-notes-iframe"
            height={frameHeight}
          />
        )}

        {tabId === PartnerFormTabItems.RELATIONS && (
          <PartnerFrontendIFrame
            path={`/partner/structure/edit/${techId}/relations${profilingWizardParams}`}
            loading={isPartnerProfileLoading}
            heightOffset={showSecondLevelTabs ? '218px' : '174px'}
          />
        )}

        {tabId === PartnerFormTabItems.DISTRIBUTION && (
          <EmbeddedIFrame
            component="pcdV2Iframe"
            url={`${runtimeConfig.configData.iframeUrls.pcdV2Iframe}${pcdV2DistributionUrl}`}
            title="Documents"
            id="structure-documents-iframe"
            height={frameHeight}
          />
        )}
        {tabId === PartnerFormTabItems.DOCUMENT_OVERVIEW && (
          <EmbeddedIFrame
            component="pcdV2Iframe"
            url={`${runtimeConfig.configData.iframeUrls.pcdV2Iframe}${pcdV2DocOverviewUrl}`}
            title="Documents"
            id="structure-documents-iframe"
            height={frameHeight}
          />
        )}

        {partner?.techId && tabId === PartnerFormTabItems.DOC_COLLECTION && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.dossierIframe}/documents/partner/${techId}`}
            title="Doc collection"
            id="doc-collection-iframe"
            height={frameHeight}
          />
        )}

        {tabId === PartnerFormTabItems.ACCOUNT_RELATIONS && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.brCoreIframe}/partner/edit/${techId}/business-relationship`}
            title={'Account Relation'.toUpperCase()}
            id="pcm-br-iframe"
            height={frameHeight}
          />
        )}

        {tabId === PartnerFormTabItems.PRODUCTS_AND_SERVICES && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.productAndServicesIframe}${pcoUrl}`}
            title={'Products & Services'.toUpperCase()}
            id="pco-iframe"
            height={frameHeight}
          />
        )}

        {tabId === PartnerFormTabItems.ACTIVITY_TIMELINE && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.catActivityTimelineIframe}/partner/activities?partnerNumber=${techId}`}
            title={'Activity Timeline'.toUpperCase()}
            id="cat-iframe"
            height={frameHeight}
          />
        )}

        {partner && tabId === PartnerFormTabItems.RISK_CONSIDERATIONS && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.riskConsiderationsIframe}/partner/${partner.techId}${profilingWizardParams}`}
            title={'Risk Considerations'.toUpperCase()}
            id="risk-considerations-iframe"
            height={frameHeight}
          />
        )}
      </TabContext>
    </>
  );
};

export default StructureFormTabs;
